import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { NeoChatContent, ProcessRunContent } from '@vivport/backend/types';
import { useMarkAsRead, useNavigateWithContentFlush } from '@/hooks';
import { useProvenanceDetail } from '@/hooks/useProvenanceQueries';
import { ContentDescriptionEditable } from '@/components/widget/content-description-editable';
import {
  ProvenanceBody,
  ProvenanceSummaryHeader,
  mapProvenanceDetailToHistoryItems,
} from '@/components/provenance';
import type {
  ManualSessionContent,
  ProvenanceContent,
} from '@/components/provenance';
import { CANONICAL_LABELS } from '@/lib/content-labels';
import { getContent } from '@/api/content-api';
import { getNeoChatConversation } from '@/api/speak2me-api';
import { toContentHref } from '@/utils/content-ref';

export type { ManualSessionContent, ProvenanceContent };

interface DetailsProvenanceProps {
  content: ProvenanceContent;
  onDescriptionSave: (description: string) => Promise<void>;
  isRegenerating: boolean;
}

export function DetailsProvenance({
  content,
  onDescriptionSave,
  isRegenerating,
}: DetailsProvenanceProps) {
  const navigate = useNavigateWithContentFlush();
  const markAsReadMutation = useMarkAsRead();
  const markedReadForContentIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (content.tracking?.isRead === true) {
      return;
    }
    if (markedReadForContentIdRef.current === content.id) {
      return;
    }
    markedReadForContentIdRef.current = content.id;
    markAsReadMutation.mutate(content.id);
  }, [content.id, content.tracking?.isRead, markAsReadMutation]);

  switch (content.contentType) {
    case 'NEO_CHAT':
      return (
        <NeoChatDetailsBody
          content={content}
          onDescriptionSave={onDescriptionSave}
          isRegenerating={isRegenerating}
          onNavigate={navigate}
        />
      );
    case 'PROCESS_RUN':
      return <ProcessRunDetailsBody content={content} onNavigate={navigate} />;
    case 'MANUAL_SESSION':
      return <ManualSessionDetailsBody content={content} onNavigate={navigate} />;
  }
}

interface NeoChatDetailsBodyProps {
  content: NeoChatContent;
  onDescriptionSave: (description: string) => Promise<void>;
  isRegenerating: boolean;
  onNavigate: (href: string) => void;
}

function NeoChatDetailsBody({
  content,
  onDescriptionSave,
  isRegenerating,
  onNavigate,
}: NeoChatDetailsBodyProps) {
  const conversationQuery = useQuery({
    queryKey: ['neoChatConversation', content.id],
    queryFn: () => getNeoChatConversation(content.id),
  });
  const items = conversationQuery.data?.conversationItems ?? [];
  const detailQuery = useProvenanceDetail(content.code);
  const backgroundWork = useMemo(
    () => mapProvenanceDetailToHistoryItems(detailQuery.data ?? null),
    [detailQuery.data]
  );

  return (
    <div data-testid="details-neo-chat" className="space-y-4">
      <ProvenanceSummaryHeader content={content} onNavigate={onNavigate} />
      <div className="flex flex-col gap-2">
        <h3 className="font-medium">Description</h3>
        <ContentDescriptionEditable
          description={content.description || ''}
          onSave={onDescriptionSave}
          disabled={isRegenerating}
          placeholder={
            isRegenerating
              ? 'Regeneration currently in progress'
              : 'Click to add description'
          }
        />
      </div>
      {backgroundWork.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="neo-chat-background-work">
          <h3 className="font-medium">Background work</h3>
          <ProvenanceBody
            mode="page"
            items={backgroundWork}
            isLoading={detailQuery.isLoading}
            isError={detailQuery.isError}
            errorMessage="Unable to load background work."
            emptyState={null}
            parentContentId={content.id}
            attachmentsBreadcrumbLabel={content.title ?? 'Conversation'}
            onOpenContent={(id) => {
              void getContent(id).then((item) => onNavigate(toContentHref(item.code)));
            }}
          />
        </div>
      )}
      <ProvenanceBody
        mode="page"
        items={items}
        isLoading={conversationQuery.isLoading}
        isError={conversationQuery.isError}
        errorMessage="Could not load conversation."
        emptyState={
          <p className="text-sm text-muted-foreground">No messages in this chat yet.</p>
        }
        parentContentId={content.id}
        attachmentsBreadcrumbLabel={CANONICAL_LABELS.document}
        onOpenContent={(id) => {
          void getContent(id).then((item) => onNavigate(toContentHref(item.code)));
        }}
      />
    </div>
  );
}

interface ProcessRunDetailsBodyProps {
  content: ProcessRunContent;
  onNavigate: (href: string) => void;
}

function ProcessRunDetailsBody({ content, onNavigate }: ProcessRunDetailsBodyProps) {
  const detailQuery = useProvenanceDetail(content.code);
  const detail = detailQuery.data ?? null;

  const projected = useMemo(
    () => mapProvenanceDetailToHistoryItems(detail),
    [detail]
  );

  return (
    <div data-testid="details-process-run" className="space-y-4">
      <ProvenanceSummaryHeader
        content={content}
        detail={detail}
        isLoadingDetail={detailQuery.isLoading}
        onNavigate={onNavigate}
      />
      <ProvenanceBody
        mode="page"
        items={projected}
        isLoading={detailQuery.isLoading}
        isError={detailQuery.isError}
        errorMessage="Unable to load run history."
        emptyState={
          <p className="text-sm text-muted-foreground">No activity recorded for this run.</p>
        }
        parentContentId={content.id}
        attachmentsBreadcrumbLabel={content.title ?? 'Process run'}
        onOpenContent={(id) => onNavigate(toContentHref(String(id)))}
      />
    </div>
  );
}

interface ManualSessionDetailsBodyProps {
  content: ManualSessionContent;
  onNavigate: (href: string) => void;
}

function ManualSessionDetailsBody({
  content,
  onNavigate,
}: ManualSessionDetailsBodyProps) {
  const detailQuery = useProvenanceDetail(content.code);
  const detail = detailQuery.data ?? null;

  const items = useMemo(
    () => mapProvenanceDetailToHistoryItems(detail),
    [detail]
  );

  return (
    <div data-testid="details-manual-session" className="space-y-4">
      <ProvenanceSummaryHeader content={content} detail={detail} />
      <ProvenanceBody
        mode="page"
        items={items}
        isLoading={detailQuery.isLoading}
        isError={detailQuery.isError}
        errorMessage="Unable to load manual session."
        emptyState={
          <p className="text-sm text-muted-foreground">No activity in this session yet.</p>
        }
        parentContentId={content.id}
        attachmentsBreadcrumbLabel={content.title ?? 'Manual session'}
        onOpenContent={(id) => onNavigate(toContentHref(String(id)))}
      />
    </div>
  );
}

